import { Component, Input, OnInit } from '@angular/core';
import { ICurrencyWithPrice, IPenalty } from 'libraries/service/resources/hotel/IResourceHotel';
import { ILabels } from 'libraries/interfaces/ILabels';
import { Yii } from 'libraries/interfaces/Yii';
import { DateMomentPipe } from 'libraries/date/dateMoment.pipe';
import { LabelService } from 'libraries/service/label/label.service';
import { HelperService } from 'libraries/hotel/services/helper.service';
import { ICost } from 'libraries/service/resources/model/search/IBooking';
import { IConfigSharedHotel } from 'libraries/hotel/models/config-shared-hotel';
import { HotelCurrencyService } from 'libraries/service/hotel-currency/hotel-currency.service';
import { ICurrency } from 'libraries/service/resources/model/search/ICurrency';
import { QuotationService } from 'libraries/hotel/services/quotation/quotation.service';
import * as moment from 'moment';
import { BtnStyleTypes } from "../../../ui_button/btn.component";
import { faBan, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import {PermissionResourceService} from "../../../service/resources/permission/permissionResource.service";

@Component({
    selector: 'penalty',
    template: `
        <ng-container *ngIf="showCostPenalty || showRetailPenalty">
            <fa-icon
                    [icon]="iconBan"
                    *ngIf="hasIcon"
                    [class.has-penalty]="isFree ? isFutureEnd : isFutureBegin"
                    [class.has-no-penalty]="isFree ? !isFutureEnd : !isFutureBegin">
            </fa-icon>
            <span [class.no-icon]="!hasIcon">
                <span *ngIf="!isFree else freePenalty">
                    <ng-container *ngIf="isUniquePenalty">{{labels.cancellationFees}}</ng-container>
                    <strong [innerHTML]="getPrice(penalty) | priceRealAmount: getPriceCurrency(penalty, currency)?.code"></strong>
                    {{ penalty.xType && showXTypeLabel ? penalty.xType === 'cost' ? labels.cost : labels.retail : '' }}
                    {{ penaltyRangeLabel }}
                </span>
                <ng-template #freePenalty>
                    <span>
                        {{ isFreeLabel }}
                    </span>
                </ng-template>
    
                <div *ngIf="penalty.text">
                    <btn (click)="toggleDetails()"
                         *ngIf="!showDetails; else hideDetailsElement"
                         [label]="labels.showDetails"
                         [type]="btnStyleTypes.actionInlineLink"></btn>
    
                    <ng-template #hideDetailsElement>
                        <btn (click)="toggleDetails()"
                             [label]="labels.hideDetails"
                             [type]="btnStyleTypes.actionInlineLink"></btn>
                    </ng-template>
    
                    <blockquote *ngIf="showDetails" [innerHTML]="penalty.text"></blockquote>
                </div>
    
                <delta-price *ngIf="previousPenalty"
                             [price]="priceValue"
                             [oldPrice]="oldPriceValue"
                             [currency]="this.currency">
                </delta-price>
            </span>
        </ng-container>
    `,
    styles: [`

        :host {
            display: flex;
            align-items: baseline;
        }

        i.fa {
            margin-right: 10px;
        }

        blockquote {
            padding: 0;
            margin: 0;
        }

        blockquote ::ng-deep ul li {
            list-style-type: disc;
        }

        blockquote ::ng-deep ul,
        blockquote ::ng-deep p {
            margin: 5px 0;
        }

        .not-available {
            color: #aaa;
        }

        .has-penalty {
            color: red;
        }

        .has-no-penalty {
            color: green;
        }
        .no-icon{
            padding-left: 22px;
        }

    `],
})
export class PenaltyComponent implements OnInit {
    @Input()
    public penalty: IPenalty;

    @Input()
    public isUniquePenalty: boolean;

    @Input()
    public previousPenalty: IPenalty;

    @Input()
    public basePrice: ICost;

    @Input()
    public configSharedHotel: IConfigSharedHotel;

    @Input()
    public currency: ICurrency;

    @Input()
    public hasIcon: boolean = true;

    public showDetails: boolean = false;

    public isFree: boolean;
    public isFutureEnd: boolean;
    public isFutureBegin: boolean;

    public isFreeLabel: string;
    public penaltyRangeLabel: string;

    public showCostPenalty: boolean = false;
    public showRetailPenalty: boolean = false;
    public showXTypeLabel: boolean = false;

    public priceValue: ICurrencyWithPrice;
    public oldPriceValue: ICurrencyWithPrice;

    public btnStyleTypes: typeof BtnStyleTypes = BtnStyleTypes;

    public iconBan: IconDefinition = faBan;

    public labels: ILabels = {
        with: Yii.t("wui-shared-js", "Avec"),
        cancellationFees: Yii.t("wui-shared-js", "Frais d'annulation de"),
        fromTo: Yii.t("wui-shared-js", "du\xa0%penaltyBegin% au\xa0%penaltyEnd%"),
        showDetails: Yii.t("wui-shared-js", "Afficher les conditions détaillées"),
        hideDetails: Yii.t("wui-shared-js", "Masquer les conditions détaillées"),
        retail: Yii.t("wui-shared-js", "(vente)"),
        cost: Yii.t("wui-shared-js", "(achat)"),
    };

    public constructor(
        private readonly dateMomentPipe: DateMomentPipe,
        private readonly labelService: LabelService,
        private readonly helperService: HelperService,
        private readonly hotelCurrencyService: HotelCurrencyService,
        private readonly quotationService: QuotationService,
        private readonly permissionResourceService: PermissionResourceService,
    ) {
    }

    public ngOnInit(): void {
        if(this.penalty.xType){
            this.permissionResourceService.getPermissionsStartingWith(['shared', 'stay', 'price']).subscribe((permissions: Set<string>): void => {
                this.showCostPenalty = (this.penalty.xType && this.penalty.xType === 'cost') && permissions.has('shared.stay.price.cost.show');
                this.showRetailPenalty = (this.penalty.xType && this.penalty.xType === 'retail') && permissions.has('shared.stay.price.retail.show');
                this.showXTypeLabel = permissions.has('shared.stay.price.cost.show') &&  permissions.has('shared.stay.price.retail.show');
            });
        } else {
            this.showCostPenalty = true;
            this.showRetailPenalty = true;
        }


        this.isFree = this.computeIsFree(this.penalty);
        if (this.penalty.validity) {
            this.isFutureBegin = this.computeIsFuture(this.penalty.validity.begin);
            this.isFutureEnd = this.computeIsFuture(this.penalty.validity.end);
        }

        if (this.isFree) {
            this.isFreeLabel = this.getFreePenaltyText(this.penalty);
        } else {
            this.penaltyRangeLabel = this.getPenaltyRange(this.penalty);
        }

        if (this.previousPenalty) {
            this.priceValue = this.getPriceValue(this.penalty);
            this.oldPriceValue = this.getPriceValue(this.previousPenalty);
        }
    }

    public getPenaltyRange(penalty: IPenalty): string {
        const isPenaltyValidity: boolean = Boolean(penalty && penalty.validity);
        const penaltyBegin: string = isPenaltyValidity
        && penalty.validity.begin
        && penalty.validity.begin !== ''
            ? this.dateMomentPipe.transform(penalty.validity.begin, this.getDateFormat(), true, true)
            : '';

        const penaltyEnd: string = isPenaltyValidity
        && penalty.validity.end
        && penalty.validity.end !== ''
            ? this.dateMomentPipe.transform(penalty.validity.end, this.getDateFormat(), true, true)
            : '';

        return penaltyBegin !== '' && penaltyEnd !== '' ?
            this.labelService.completeLabel(
                this.labels.fromTo,
                {penaltyBegin, penaltyEnd}) :
            '';
    }

    public getDateFormat(): string {
        return this.helperService.getDateFormat().momentShort;
    }

    public getPriceValue(penalty: IPenalty): ICurrencyWithPrice {
        return penalty.total && penalty.total.cost ? penalty.total.cost :
            this.getPercentedPrice(penalty);
    }

    public getPrice(penalty: IPenalty): number {
        const price: ICurrencyWithPrice = this.getPriceValue(penalty);
        const currency: ICurrency = this.getPriceCurrency(penalty, this.currency);

        if (this.configSharedHotel) {
            currency.code = this.hotelCurrencyService.getCurrencyString(price, this.configSharedHotel);
        }
        return this.quotationService.getPrice(price, currency);
    }

    public getPriceCurrency(penalty: IPenalty, currency: ICurrency): ICurrency {
        const value: ICurrencyWithPrice = this.getPriceValue(penalty);

        if (value && currency && currency.code && value[currency.code] !== undefined) {
            return currency;
        }

        return {
            code: Object.keys(value).find(() => true),
            decimals: 2
        };
    }

    public computeIsFuture(date: string): boolean {
        return moment(date).isAfter();
    }

    public toggleDetails(): void {
        this.showDetails = !this.showDetails;
    }

    public computeIsFree(penalty: IPenalty): boolean {
        const costPerCurrency: number[] = Object.values(penalty.total ? penalty.total.cost : {});
        return !penalty.percentage
            && (costPerCurrency.length === 0
                || costPerCurrency.every((value: number) => value === 0));
    }

    public computeIsUnknown(penalty: IPenalty): boolean {
        return !Boolean(penalty.total);
    }

    public getFreePenaltyText(penalty: IPenalty): string {
        return this.labelService.completeLabel(
            this.computeIsUnknown(penalty) ?
                Yii.t('wui-shared-js', "Frais inconnus jusqu'au\xa0%date%"):
                Yii.t('wui-shared-js', "Annulation gratuite jusqu'au\xa0%date%"),
            {
                date: this.dateMomentPipe.transform(penalty.validity.end, this.getDateFormat(), true, true)
            }
        );
    }

    private getPercentedPrice(penalty: IPenalty): ICurrencyWithPrice {
        const priceKeys: string[] = Object.keys(this.basePrice);
        return priceKeys.reduce(
            (acc: ICurrencyWithPrice, priceKey: string) => {
                return {
                    ...acc,
                    [priceKey]: +this.basePrice[priceKey] * +penalty.percentage / 100
                };
            },
            {}
        );
    }
}